import React, { useEffect, useCallback, useState, useRef, useMemo } from "react";
import { connect, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import SiteWrapper from "components/Layouts/SiteWrapper";
import DetailsPanel from "components/Elements/DetailsPanel";
import DeleteDialog from "components/Elements/DeleteDialog";
import TabTitle from "components/Elements/TabTitle";
import GroupedTable from "components/Modules/GroupedTable";
import { FilterProvider, FilterContainer, useFilters } from "components/Elements/Filters";
import { useFeatureFlags } from "contexts/FeatureFlagsContext";
import { FEATURE_FLAGS } from "constants/FeatureFlags";

import { fetchSiteDetail } from "../../../../redux/siteDetail/actions";
import { showError } from "../../../../redux/common/actions";
import { BASE_API_PATH, AccessTypes } from "helpers/constants";
import { getLocalStorageData } from "helpers/utils";
import { getSiteNavigationFromStorage } from "helpers/siteNavigation";
import { getSiteApplicationDetail } from "services/clients/sites/siteApplications/siteApplicationDetails";

import useGroupedAssetData from "./hooks/useGroupedAssetData";
import useTableConfig from "./hooks/useTableConfig";
import useAssetActions from "./hooks/useAssetActions";
import useShowAssetReferences from "./hooks/useShowAssetReferences";
import AssetDialog from "./dialogs/AssetDialog";
import ImportDialog from "./dialogs/ImportDialog";
import SearchBox from "./components/SearchBox";

const useStyles = makeStyles()((theme) => ({
	container: { padding: "20px 0" },
	detailsContainer: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "flex-start",
		marginBottom: "20px",
		"@media (max-width: 768px)": { flexDirection: "column", gap: "15px" },
	},
	searchRow: { display: "flex", alignItems: "center", gap: "8px" },
}));

const AssetRegisterPage = ({ fetchCrumbs, getError, siteDetails }) => {
	const { id } = useParams();
	const { siteAppID } = getLocalStorageData("me");
	const [maxAssetLevel, setMaxAssetLevel] = useState(null);

	useEffect(() => {
		if (!siteAppID) return;
		let cancelled = false;
		const fetchMaxLevel = async () => {
			try {
				const response = await getSiteApplicationDetail(siteAppID);
				if (!cancelled && response.status) {
					setMaxAssetLevel(response.data?.maxAssetLevel ?? 0);
				}
			} catch {
				if (!cancelled) setMaxAssetLevel(null);
			}
		};
		fetchMaxLevel();
		return () => { cancelled = true; };
	}, [siteAppID]);

	const { isEnabled } = useFeatureFlags();
	const treeViewEnabled = isEnabled(FEATURE_FLAGS.SITE_ASSET_REGISTER_TREE_VIEW);
	const hasMultipleLevels = treeViewEnabled && (maxAssetLevel === null || maxAssetLevel === 0 || maxAssetLevel > 1);

	const filterConfig = useMemo(() => {
		const filters = {};
		if (hasMultipleLevels) {
			filters.assetGrouping = { enabled: true };
			filters.level = { showAll: true, maxAssetLevel: maxAssetLevel || 8 };
			filters.assetViewToggle = { enabled: true };
		}
		return { filters };
	}, [hasMultipleLevels]);

	return (
		<FilterProvider config={filterConfig} storageKey={`assetRegister-${id}`}>
			<AssetRegisterContent
				fetchCrumbs={fetchCrumbs}
				getError={getError}
				siteDetails={siteDetails}
				hasMultipleLevels={hasMultipleLevels}
			/>
		</FilterProvider>
	);
};

const AssetRegisterContent = ({ fetchCrumbs, getError, siteDetails, hasMultipleLevels }) => {
	const { classes } = useStyles();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { id, clientId } = useParams();
	const { filterValues } = useFilters();

	const { position, siteAppID, customCaptions } = getLocalStorageData("me");
	const navigation = getSiteNavigationFromStorage(getLocalStorageData);
	const showAssetReferences = useShowAssetReferences(siteDetails);
	const showAddImport = position?.assetAccess === AccessTypes.Full || !siteAppID;

	const [searchTerm, setSearchTerm] = useState("");
	const [currentTableSort, setCurrentTableSort] = useState(["assetNumber", "asc"]);
	const [page, setPage] = useState({ pageNo: 1, perPage: 25 });
	const [modal, setModal] = useState({ delete: false, edit: false, add: false, import: false });
	const [selectedRow, setSelectedRow] = useState(null);
	const [editData, setEditData] = useState({});

	const level = hasMultipleLevels ? (filterValues.level?.value?.id || null) : null;
	const grouping = hasMultipleLevels ? (filterValues.assetGrouping?.value?.id || null) : null;
	const expandColumnIndex = showAssetReferences ? 1 : 0;
	const isSearching = searchTerm && searchTerm.trim() !== "";
	const isTreeView = (filterValues.assetViewToggle?.value?.id || "tree") === "tree"
		&& (!isSearching || hasMultipleLevels);

	const assetData = useGroupedAssetData(id, customCaptions, level, grouping);
	const { tableConfig, getEditableData } = useTableConfig(showAssetReferences, customCaptions);

	const initializedRef = useRef(false);
	const assetDataRef = useRef();
	assetDataRef.current = assetData;
	const searchTermRef = useRef(searchTerm);
	searchTermRef.current = searchTerm;

	const actions = useAssetActions({
		assetData, getEditableData,
		setSelectedRow, setEditData, setModal,
	});

	const handleSearch = useCallback((searchText) => { setSearchTerm(searchText); }, []);

	const handleSort = useCallback(async (sortField, sortOrder) => {
		setCurrentTableSort([sortField, sortOrder]);
		await assetDataRef.current.fetchAssets(1, sortField, sortOrder, searchTerm);
		setPage({ pageNo: 1, perPage: 25 });
	}, [searchTerm]);

	const handlePageChange = useCallback(async (pageNo, prevData, sortOptions) => {
		const result = await assetDataRef.current.loadMoreAssets(pageNo, prevData, sortOptions, searchTermRef.current);
		if (result.success) setPage({ pageNo, perPage: 25 });
		return result;
	}, []);

	const handleViewChange = useCallback((isTree) => {
		if (!isTree) {
			assetData.collapseAll();
		}
	}, [assetData]);

	const tableData = useMemo(() => {
		return assetData.data.map((row) => ({ ...row, displayData: row }));
	}, [assetData.data]);

	// Re-fetch when search or sort changes (skip on mount — initial load handles it)
	useEffect(() => {
		if (!initializedRef.current) return;
		if (searchTerm.trim() === "") {
			assetDataRef.current.fetchAssets(1, currentTableSort[0], currentTableSort[1], "");
		} else {
			assetDataRef.current.searchAssets(searchTerm, currentTableSort[0], currentTableSort[1]);
		}
		setPage({ pageNo: 1, perPage: 25 });
	}, [searchTerm, currentTableSort]);

	// Initial data load
	useEffect(() => {
		const initializeData = async () => {
			try {
				await fetchCrumbs(id, clientId);
				await assetData.fetchAssets(1, "assetNumber", "asc", "");
				initializedRef.current = true;
			} catch (err) {
				dispatch(showError(`Failed to load ${customCaptions?.assetPlural || "assets"}.`));
			}
		};
		initializeData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id, clientId]);

	// Re-fetch when level filter changes (skip on mount)
	useEffect(() => {
		if (!initializedRef.current || level === undefined) return;
		assetDataRef.current.fetchAssets(1, currentTableSort[0], currentTableSort[1], searchTerm);
		setPage({ pageNo: 1, perPage: 25 });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [level]);

	// Re-fetch when grouping mode changes (skip on mount)
	useEffect(() => {
		if (!initializedRef.current || !grouping) return;
		assetDataRef.current.fetchAssets(1, currentTableSort[0], currentTableSort[1], searchTerm);
		setPage({ pageNo: 1, perPage: 25 });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [grouping]);

	if (position?.assetAccess === AccessTypes.None) {
		navigate(-1);
		return null;
	}

	const onSuccess = async () => { await actions.refreshData(); setPage({ pageNo: 1, perPage: 25 }); };
	const onDeleteSuccess = async () => { await actions.refreshData(); actions.closeModal("delete"); setPage({ pageNo: 1, perPage: 25 }); };

	const pageTitle = `${siteDetails?.name || "Site"} ${customCaptions?.assetPlural || "Assets"}`;
	const deleteEndpoint = `${BASE_API_PATH}SiteAssetRegisters`;

	return (
		<>
			<TabTitle title={pageTitle} />
			<DeleteDialog entityName={customCaptions?.asset || "Asset"} open={modal.delete} deleteID={selectedRow?.id} deleteEndpoint={deleteEndpoint} handleRemoveData={onDeleteSuccess} closeHandler={() => actions.closeModal("delete")} />
			<AssetDialog testIdPrefix="site-assets-edit" open={modal.edit} closeHandler={() => actions.closeModal("edit")} onSuccess={onSuccess} getError={getError} isEdit showSAPColumns={showAssetReferences} siteId={id} data={editData} />
			<AssetDialog testIdPrefix="site-assets-add" open={modal.add} closeHandler={() => actions.closeModal("add")} onSuccess={onSuccess} getError={getError} isEdit={false} showSAPColumns={showAssetReferences} siteId={id} data={{}} />
			<ImportDialog testIdPrefix="site-assets-import" open={modal.import} closeHandler={() => actions.closeModal("import")} siteId={id} onImportSuccess={onSuccess} getError={getError} />

			<SiteWrapper testIdPrefix="site-assets" current={customCaptions?.assetPlural || "Assets"} navigation={navigation} onNavClick={(urlToGo) => navigate(`/app/clients/${clientId}/sites/${id}${urlToGo}`)} status="" lastSaved="" showAdd={showAddImport} showImport={showAddImport} onClickImport={actions.handleImport} onClickAdd={actions.handleAdd}>
				<div className={classes.container}>
					<div className={classes.detailsContainer}>
						<DetailsPanel testIdPrefix="site-assets-details" header={customCaptions?.assetPlural || "Assets"} dataCount={assetData.count} description={`View and manage all ${customCaptions?.assetPlural?.toLowerCase() || "assets"} in the asset register`} />
						<div className={classes.searchRow}>
							<SearchBox testId="site-assets-search-input" value={searchTerm} onSearch={handleSearch} placeholder={`Search ${customCaptions?.assetPlural || "Assets"}`} delay={500} />
						</div>
					</div>

					{hasMultipleLevels && (
						<div style={{ marginTop: 16, marginBottom: 16, overflow: "hidden" }}>
							<FilterContainer />
						</div>
					)}

					<GroupedTable
						data={tableData}
						childData={assetData.childData}
						expandedRows={assetData.expandedRows}
						onExpandToggle={assetData.toggleExpand}
						childLoading={assetData.childLoading}
						tableConfig={tableConfig}
						expandColumnIndex={expandColumnIndex}
						onEdit={actions.handleEdit}
						onDelete={actions.handleDelete}
						onDownloadReport={actions.handleDownloadReport}
						onExpandAll={hasMultipleLevels ? assetData.expandAll : undefined}
						onCollapseAll={hasMultipleLevels ? assetData.collapseAll : undefined}
						bulkLoading={assetData.bulkLoading}
						page={page.pageNo}
						onPageChange={handlePageChange}
						count={assetData.count}
						isLoading={assetData.isLoading}
						searchText={searchTerm}
						isReadOnly={!showAddImport}
						currentTableSort={currentTableSort}
						handleSort={handleSort}
						customCaptions={customCaptions}
						onViewChange={handleViewChange}
						showTreeControls={hasMultipleLevels}
						isTreeView={isTreeView}
						testIdPrefix="site-assets-grid"
					/>
				</div>
			</SiteWrapper>
		</>
	);
};

const mapStateToProps = ({ siteDetailData: { siteDetails } }) => ({ siteDetails });
const mapDispatchToProps = (dispatch) => ({
	fetchCrumbs: (id, clientId) => dispatch(fetchSiteDetail(id, clientId)),
	getError: (msg) => dispatch(showError(msg)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AssetRegisterPage);
